import { type ElementTypes } from '@bizzdesign/modeler/models';
import { type } from '@ngrx/signals';
import { eventGroup } from '@ngrx/signals/events';

export type HorizontalDirection = 'left' | 'center' | 'right';
export type VerticalDirection = 'top' | 'middle' | 'bottom';
export type ToggleStatus = 'on' | 'off';
export type ZOrderLocation = 'frame_burger_menu' | 'more_menu';
export type CommandStatus = 'undo' | 'redo';
export type ZOrderPayload = {
  location?: ZOrderLocation;
  commandStatus?: CommandStatus;
  triggered_from_keyboard?: boolean;
};

export const contextMenuUiEvents = eventGroup({
  source: 'Modeler Context Menu UI',
  events: {
    relatedObjectsChecked: type<number>(),
    relatedObjectsLoaded: type<void>(),
  },
});

export const swapElementUiEvents = eventGroup({
  source: 'Modeler Swap Menu UI',
  events: {
    freeFormToBdDraftSwapped: type<void>(),
    freeFormToFromRepoSwapped: type<void>(),
    bdDraftToFreeFormSwapped: type<void>(),
    bdDraftToFromRepoSwapped: type<void>(),
    lineToBdRelationSwapped: type<void>(),
    bdRelationToLineSwapped: type<void>(),
  },
});

export const hyperlinkNotificationUiEvents = eventGroup({
  source: 'Modeler Hyperlink Notification UI',
  events: {
    hyperlinkRemovedOnShapeChange: type<{ commandId: string }>(),
  },
});

export const commandHistoryUiEvents = eventGroup({
  source: 'Modeler Command History UI',
  events: {
    commandExecuted: type<{ commandId: string }>(),
  },
});

export const styleElementUiEvents = eventGroup({
  source: 'Modeler Style Menu Buttons UI',
  events: {
    colorChanged: type<{ color: string | string[] }>(),
    fontTypeChanged: type<{ font: string | string[] }>(),
    fontSizeChanged: type<{ size: number | number[] }>(),
    fontBoldChanged: type<{
      status: ToggleStatus | ToggleStatus[];
      triggered_from_keyboard?: boolean;
    }>(),
    fontItalicChanged: type<{
      status: ToggleStatus | ToggleStatus[];
      triggered_from_keyboard?: boolean;
    }>(),
    fontUnderlineChanged: type<{
      status: ToggleStatus | ToggleStatus[];
      triggered_from_keyboard?: boolean;
    }>(),
    fontStrikethroughChanged: type<{
      status: ToggleStatus | ToggleStatus[];
      triggered_from_keyboard?: boolean;
    }>(),
    horizontalAlignmentChanged: type<{
      direction: HorizontalDirection | HorizontalDirection[];
      elementType?: ElementTypes | ElementTypes[];
    }>(),
    verticalAlignmentChanged: type<{
      direction: VerticalDirection | VerticalDirection[];
      elementType?: ElementTypes | ElementTypes[];
    }>(),
  },
});

export const zOrderUiEvents = eventGroup({
  source: 'Modeler ZOrder UI',
  events: {
    bringToFrontTriggered: type<ZOrderPayload>(),
    bringForwardTriggered: type<ZOrderPayload>(),
    sendBackwardTriggered: type<ZOrderPayload>(),
    sendToBackTriggered: type<ZOrderPayload>(),
  },
});

/**
 * The UI surface a grouping action (group or ungroup) was triggered from.
 *
 * Reported as the `location` property on {@link GroupPayload}, so product
 * analytics can attribute group/ungroup usage to the entry point the user
 * took.
 *
 * @remarks
 * - `context_menu` — the context menu on the canvas.
 * - `frame_burger_menu` — the burger menu of a frame.
 * - `more_menu` — more menu in the context menu or on right click.
 */
export type GroupingLocation =
  | 'context_menu'
  | 'frame_burger_menu'
  | 'more_menu';

export type GroupPayload = {
  commandStatus?: CommandStatus;
  triggered_from_keyboard?: boolean;
  location?: GroupingLocation;
};

export const groupUiEvents = eventGroup({
  source: 'Modeler Group UI',
  events: {
    groupTriggered: type<GroupPayload>(),
    ungroupTriggered: type<GroupPayload>(),
    /**
     * Dispatched when an ungroup is blocked because a peer is currently editing a nested element
     * inside the group (`GroupInteractor.onUngroup`). `peerName` is the blocking peer's resolved
     * display name, for the toast that explains why the action was refused.
     */
    ungroupBlocked: type<{ peerName: string }>(),
  },
});

export const graphActionUiEvents = eventGroup({
  source: 'Modeler Graph Action UI',
  events: {
    addToFrameTriggered: type<{ triggered_from_keyboard?: boolean }>(),
  },
});

export type StickyAuthorLocation = 'context_menu' | 'frame_burger_menu';
export type StickyAuthorPayload = {
  location: StickyAuthorLocation;
  status: ToggleStatus;
};

export const stickyAuthorUiEvents = eventGroup({
  source: 'Modeler Sticky Author UI',
  events: {
    stickyAuthorVisibilityToggled: type<StickyAuthorPayload>(),
  },
});

export const elementRenameUiEvents = eventGroup({
  source: 'Modeler Element Rename UI',
  events: {
    elementRenamed: type<{
      elementType: string;
      numberOfHyperlinks: number;
      textLength: number;
    }>(),
  },
});

/**
 * The interaction that opened a hyperlink element's URL.
 *
 * @remarks `tooltip` and `context_menu` are frame-only; `view_click` is a plain
 * left click on a published view; `alt_click` is the Alt/Option + click shortcut.
 */
export type HyperlinkOpenLocation =
  | 'tooltip'
  | 'context_menu'
  | 'view_click'
  | 'alt_click';

/**
 * Payload identifying the hyperlink element the pointer entered.
 *
 * @property id - graphic id, matched against the selection and against `data-graphic-id` on the rendered node
 * @property semanticsId - semantics id, the other half of the rendered node's selector
 * @property url - the element name normalized to an absolute URL
 * @remarks `id` and `semanticsId` are named to match the element shape that
 * `getElement` in `@bizzdesign/modeler/utils-toolbar` resolves positions from.
 */
export type HyperlinkHoverPayload = {
  id: string;
  semanticsId: string | undefined;
  url: string;
};

export const hyperlinkUiEvents = eventGroup({
  source: 'Modeler Hyperlink UI',
  events: {
    elementHovered: type<HyperlinkHoverPayload>(),
    elementUnhovered: type<void>(),
    hyperlinkOpened: type<{
      location: HyperlinkOpenLocation;
      // The frame the link was opened from, or the view id when a published view is opened.
      originId: string | undefined;
    }>(),
  },
});
